import Papa from 'papaparse';
import type { MatchSiteType } from '../types';

export type BulkMatchCsvRow = {
  rowNumber: number;
  opponent: string;
  matchDate: string;
  matchTime: string;
  siteType: MatchSiteType;
  facility: string;
  classification: string;
  warmupMinutes: number;
  groupmeEventId: string;
  notes: string;
};

export type BulkMatchIssue = {
  severity: 'error' | 'warning';
  message: string;
};

export type BulkMatchPreviewRow = BulkMatchCsvRow & {
  facilityId: string | null;
  facilityText: string | null;
  issues: BulkMatchIssue[];
};

export type BulkMatchValidationContext = {
  teamId: string;
  defaultFacilityId: string | null;
  defaultWarmupMinutes: number;
  facilities: { id: string; name: string }[];
  existingMatches: {
    id: string;
    teamId: string;
    opponent: string;
    matchDate: string;
    groupmeEventId: string | null;
  }[];
  existingEventGroupMeIds: string[];
};

export type BulkMatchParseResult = {
  rows: BulkMatchCsvRow[];
  errors: string[];
};

const expectedHeaders = [
  'opponent',
  'match_date',
  'match_time',
  'site_type',
  'facility',
  'classification',
  'warmup_minutes',
  'groupme_event_id',
  'notes',
] as const;

const classificationAliases = new Map([
  ['regular season', 'regular_season'],
  ['regular_season', 'regular_season'],
  ['playoff', 'playoff'],
  ['tournament', 'tournament'],
  ['practice', 'practice'],
  ['scrimmage', 'scrimmage'],
  ['other', 'other'],
]);

function normalizedKey(value: string): string {
  return value.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, '_');
}

function normalizedText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function normalizeDate(value: string): string {
  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const parts = iso
    ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
    : us
      ? [Number(us[3]), Number(us[1]), Number(us[2])]
      : null;
  if (!parts) return '';
  const [year, month, day] = parts;
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return '';
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function normalizeMatchTime(value: string): string {
  const match = value.trim().match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap]\.?m\.?)?$/i);
  if (!match) return '';
  let hour = Number(match[1]);
  const minute = Number(match[2] || '0');
  const meridiem = match[3]?.replace(/\./g, '').toLowerCase();
  if (minute < 0 || minute > 59) return '';
  if (meridiem) {
    if (hour < 1 || hour > 12) return '';
    if (meridiem === 'pm' && hour !== 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;
  } else if (hour < 0 || hour > 23) {
    return '';
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function normalizeSiteType(value: string): MatchSiteType {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'home') return 'Home';
  if (normalized === 'away') return 'Away';
  if (normalized === 'neutral') return 'Neutral';
  return 'Unknown';
}

function normalizeClassification(value: string): string {
  return classificationAliases.get(value.trim().toLowerCase()) || '';
}

export function parseBulkMatchCsv(csvText: string): BulkMatchParseResult {
  const parsed = Papa.parse<Record<string, unknown>>(csvText.trim(), {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: normalizedKey,
  });
  const headers = new Set((parsed.meta.fields || []).map(normalizedKey));
  const missingRequiredHeaders = ['opponent', 'match_date', 'match_time'].filter((header) => !headers.has(header));
  const errors = [
    ...parsed.errors.map((error) => `CSV row ${(error.row ?? 0) + 2}: ${error.message}`),
    ...(missingRequiredHeaders.length > 0
      ? [`Missing required CSV column${missingRequiredHeaders.length === 1 ? '' : 's'}: ${missingRequiredHeaders.join(', ')}`]
      : []),
  ];

  const rows = parsed.data.map((record, index) => {
    const warmupRaw = normalizedText(record.warmup_minutes);
    return {
      rowNumber: index + 2,
      opponent: normalizedText(record.opponent),
      matchDate: normalizeDate(normalizedText(record.match_date)),
      matchTime: normalizeMatchTime(normalizedText(record.match_time)),
      siteType: normalizeSiteType(normalizedText(record.site_type)),
      facility: normalizedText(record.facility),
      classification: normalizedText(record.classification)
        ? normalizeClassification(normalizedText(record.classification))
        : 'regular_season',
      warmupMinutes: warmupRaw === '' ? Number.NaN : Number(warmupRaw),
      groupmeEventId: normalizedText(record.groupme_event_id),
      notes: normalizedText(record.notes),
    };
  });

  if (rows.length === 0 && errors.length === 0) errors.push('The CSV does not contain any match rows.');
  return { rows, errors };
}

export function validateBulkMatchRows(
  rows: BulkMatchCsvRow[],
  context: BulkMatchValidationContext,
): BulkMatchPreviewRow[] {
  const facilitiesByName = new Map(
    context.facilities.map((facility) => [facility.name.trim().toLowerCase(), facility]),
  );
  const existingGroupMeIds = new Set([
    ...context.existingMatches.map((match) => match.groupmeEventId || '').filter(Boolean),
    ...context.existingEventGroupMeIds.filter(Boolean),
  ]);
  const importGroupMeCounts = rows.reduce<Map<string, number>>((counts, row) => {
    if (row.groupmeEventId) counts.set(row.groupmeEventId, (counts.get(row.groupmeEventId) || 0) + 1);
    return counts;
  }, new Map());

  return rows.map((row) => {
    const issues: BulkMatchIssue[] = [];
    const facilityMatch = row.facility ? facilitiesByName.get(row.facility.toLowerCase()) : undefined;
    const facilityId = facilityMatch?.id
      || (row.siteType === 'Home' && !row.facility ? context.defaultFacilityId : null);
    const facilityText = row.facility && !facilityMatch ? row.facility : null;
    const warmupMinutes = Number.isInteger(row.warmupMinutes)
      ? row.warmupMinutes
      : context.defaultWarmupMinutes;

    if (!row.opponent) issues.push({ severity: 'error', message: 'Opponent is required.' });
    if (!row.matchDate) issues.push({ severity: 'error', message: 'Match date is invalid.' });
    if (!row.matchTime) issues.push({ severity: 'error', message: 'Match time is invalid.' });
    if (!row.classification || ![...classificationAliases.values()].includes(row.classification)) {
      issues.push({ severity: 'error', message: 'Classification is invalid.' });
    }
    if (warmupMinutes < 0 || warmupMinutes > 240) {
      issues.push({ severity: 'error', message: 'Warmup minutes must be between 0 and 240.' });
    }
    if (row.groupmeEventId) {
      if (existingGroupMeIds.has(row.groupmeEventId)) {
        issues.push({ severity: 'error', message: 'GroupMe Event ID is already mapped to an active CourtCaptain event.' });
      }
      if ((importGroupMeCounts.get(row.groupmeEventId) || 0) > 1) {
        issues.push({ severity: 'error', message: 'GroupMe Event ID is duplicated in this import.' });
      }
    }
    const duplicate = context.existingMatches.some((match) => (
      match.teamId === context.teamId
      && match.matchDate === row.matchDate
      && match.opponent.trim().toLowerCase() === row.opponent.trim().toLowerCase()
    ));
    const duplicateInImport = rows.some((candidate) => (
      candidate !== row
      && candidate.matchDate === row.matchDate
      && candidate.opponent.trim().toLowerCase() === row.opponent.trim().toLowerCase()
    ));
    if (duplicate || duplicateInImport) {
      issues.push({ severity: 'warning', message: 'Possible duplicate match detected.' });
    }

    const inferredSiteType = row.siteType === 'Unknown' && facilityId && context.defaultFacilityId
      ? facilityId === context.defaultFacilityId ? 'Home' : 'Away'
      : row.siteType;

    return {
      ...row,
      siteType: inferredSiteType,
      warmupMinutes,
      facilityId,
      facilityText,
      issues,
    };
  });
}

export function bulkMatchCsvTemplate(): string {
  return `${expectedHeaders.join(',')}\nStonebridge Ranch,2026-07-12,4:00 PM,Away,Stonebridge Ranch,Regular Season,30,123456789,\nPrestonwood,2026-07-19,4:00 PM,Home,Courts of McKinney,Regular Season,30,987654321,Bring water`;
}
